<script setup lang="ts">
  import { MpUIProvider } from '@borg/ui';
  import { en, hr } from 'mp-poll';
  import { initGoogleOneTapLogin } from '@/utils/googleAccount';
  import { provideAuthForm } from '@/components/Auth/useAuthForm';
  import { provideHistory } from '@/components/JobSearchBar/useHistory';
  import { openManageCookiesPolicyModal } from '@/components/ManageCookiesPolicyModal.vue';

  provideAuthForm();
  provideHistory();

  const { $isProd } = useNuxtApp();
  const i18n = useI18n();
  const route = useRoute();
  const getRouteBaseName = useRouteBaseName();
  const domain = useDomain();
  const siteTheme = useSiteTheme();
  const config = useRuntimeConfig();
  const projectConfig = useProjectConfig();
  const lang = computed(() => i18n.locale.value);
  const thisUrl = computed(() => `${domain.value}${route.fullPath}`);
  const assetsBaseUrl = computed(() => config.public.assetsBaseUrl);
  const projectName = computed(() => config.public.project.name ?? 'generic');
  const lightStyles = computed(() => `${assetsBaseUrl.value}/brand/${projectName.value}/light.css`);
  const darkStyles = computed(() => `${assetsBaseUrl.value}/brand/${projectName.value}/dark.css`);
  const headMeta = computed(() => {
    const headMeta = projectConfig.headMeta;
    if (!$isProd) {
      headMeta.push({ name: 'robots', content: 'noindex' });
    }
    return Array.from(new Set(headMeta.map((obj) => JSON.stringify(obj)))).map((e) =>
      JSON.parse(e),
    );
  });

  i18n.mergeLocaleMessage('hr', hr);
  i18n.mergeLocaleMessage('en', en);

  const ogImage = projectConfig.parameters.siteOgImage
    ? {
        ogImage: projectConfig.parameters.siteOgImage.url,
        ogImageWidth: projectConfig.parameters.siteOgImage.width,
        ogImageHeight: projectConfig.parameters.siteOgImage.height,
      }
    : undefined;

  const routeBaseName = computed(() => getRouteBaseName(route) ?? '');

  useHead({
    htmlAttrs: { lang, 'data-theme': () => siteTheme.theme, 'data-route': routeBaseName },
    meta: [
      ...headMeta.value,
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0 user-scalable=0' },
    ],
    title: projectConfig.title,
  });

  useSeoMeta({
    ogUrl: thisUrl,
    ogType: 'website',
    ...ogImage,
  });

  if (projectConfig.features.almaCookieManager) {
    _almaCookiesSettings.provide();
  }

  onMounted(() => {
    initGoogleOneTapLogin();
    if (projectConfig.features.almaCookieManager) {
      // Alma cookie scripts loaded in AlmaCookiesScript
    } else {
      // Open regular cookie modal
      openManageCookiesPolicyModal();
    }
  });
</script>

<template>
  <Head>
    <Link
      rel="icon"
      :href="`${projectConfig.parameters.favicon}`"
    />
    <Link
      rel="stylesheet"
      href="/site.css"
    />
    <Link
      rel="preload"
      as="style"
      href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Source+Sans+Pro:wght@300;400;600;700&display=swap"
    />
    <Link
      rel="stylesheet"
      href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Source+Sans+Pro:wght@300;400;600;700&display=swap"
    />
    <Link
      rel="stylesheet"
      :href="lightStyles"
    />
    <Link
      rel="stylesheet"
      :href="darkStyles"
    />
    <GoogleScript />
    <FacebookScript />
    <AlmaCookiesScript v-if="projectConfig.features.almaCookieManager" />
    <BannerScript v-if="projectConfig.features.bannerScript" />
    <Style> html, body, body > :first-child { height: 100%; } </Style>
  </Head>
  <MpUIProvider :theme="siteTheme.theme">
    <slot />
  </MpUIProvider>
</template>
